import { computed, inject, Injectable, linkedSignal, signal } from "@angular/core";
import { VolunteerDetails, VolunteerInterviewForm, VolunteerStatusForm, VolunteerStatusId, VolunteerTopicForm } from "@interfaces";
import { AppService, FuncService } from "../../../../services";


@Injectable({ providedIn: 'root' })
export class VolunteerDialogDataService {

	private func = inject(FuncService);
	public app = inject(AppService);
	public readonly volunteerDetails = signal<VolunteerDetails | undefined>(undefined);
	public readonly volunteerTopics = computed(() => this.volunteerDetails()?.volunteerTopics ?? []);

	public canRecruitVolunteers = computed(() => this.app.data().permissions.canRecruitVolunteers);
	public canCoordinateVolunteers = computed(() => this.app.data().permissions.canCoordinateVolunteers);
	public canManageVolunteerStaff = computed(() => this.app.data().permissions.canManageVolunteerStaff);

	// No access if user cannot recruit or coordinate volunteers
	public hasVolunteerEditPermissions = computed(() => this.canRecruitVolunteers() || this.canCoordinateVolunteers());

	public readonly isLeadVolunteer = computed(() => {
		const volunteer = this.volunteerDetails();
		if (!volunteer) return false;
		return volunteer!.volunteerStatusId === VolunteerStatusId.Pending || volunteer!.volunteerStatusId === VolunteerStatusId.Rejected;
	})

	public readonly overviewContentView = linkedSignal<boolean, 'lead' | 'volunteer'>({
		source: this.isLeadVolunteer,
		computation: (isLead, previous) => {
			const newValue = isLead ? 'lead' : 'volunteer';
			return previous?.value === newValue ? previous.value : newValue;
		},
	});

	public async getVolunteerDetails(volunteerId: number) {
		const results = await this.func.volunteer.getVolunteerDetails({ volunteerId });
		this.volunteerDetails.set(results);
	};

	public async reloadData() {
		const volunteerId = this.volunteerDetails()?.volunteerId;
		if (volunteerId) {
			await this.getVolunteerDetails(volunteerId);
		}
	}

	public async getFileData(personId: number, videoId: number) {
		if (!personId) return undefined;
		return this.func.videoRecording.getVideo({
			personId,
			videoId,
			type: 'volunteer',
		});
	}

	public async addVolunteerInterview(payload: { interview: VolunteerInterviewForm, volunteerId: number }) {
		const results = await this.func.volunteer.addVolunteerInterview(payload);
		if (results && payload.volunteerId) {
			this.getVolunteerDetails(payload.volunteerId);
		}
	}

	public async updateVolunteerInterview(payload: { volunteerId: number, interviewId: number, interview: VolunteerInterviewForm }) {
		await this.func.volunteer.updateVolunteerInterview({ interviewId: payload.interviewId, interview: payload.interview });
		if (payload.volunteerId) {
			await this.getVolunteerDetails(payload.volunteerId);
		}
	}

	public async updateVolunteerInterviewDateTime(payload: { interviewDateUTC: number | undefined, volunteerId: number }) {
		await this.func.volunteer.updateVolunteerInterviewDateTime({ interviewDateUTC: payload.interviewDateUTC!, volunteerId: payload.volunteerId });
		if (payload.volunteerId) {
			this.getVolunteerDetails(payload.volunteerId);
		}
	}

	public async volunteerStatusUpdate(payload: VolunteerStatusForm) {
		await this.func.volunteer.updateVolunteerStatus({ ...payload });
		if (payload.volunteerId) {
			this.getVolunteerDetails(payload.volunteerId);
		}
	}

	public async addVolunteerTopic(payload: VolunteerTopicForm) {
		const volunteerTopicId = await this.func.volunteer.topic.add(payload);
		return volunteerTopicId;
	}

	public async updateVolunteerTopic(payload: VolunteerTopicForm) {
		const volunteerTopicId = await this.func.volunteer.topic.update(payload);
		return volunteerTopicId;
	}

	public async getTopicById(volunteerTopicId: number) {
		return await this.func.volunteer.topic.get({ volunteerTopicId });
	}
}